import React from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import { toggleDarkMode, toggleShowMilliseconds, setNotificationSound, toggleNotifications, setLanguage, updateSettings } from '../../store/settingsSlice';
import '../atoms/Switch.css';
import Button from '../atoms/Button';
import { FaMoon, FaSun, FaClock, FaBell, FaBellSlash, FaLanguage } from 'react-icons/fa';
import { useAppDispatch } from '../../hooks/useAppDispatch';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const settings = useSelector((state: RootState) => state.settings);
  const { t } = useTranslation();
  
  // 切换暗色模式
  const handleToggleDarkMode = () => {
    dispatch(toggleDarkMode());
    // 同时保存到数据库
    dispatch(updateSettings({ darkMode: !settings.darkMode }));
  };
  
  // 切换显示毫秒
  const handleToggleShowMilliseconds = () => {
    dispatch(toggleShowMilliseconds());
    // 同时保存到数据库
    dispatch(updateSettings({ showMilliseconds: !settings.showMilliseconds }));
  };
  
  // 设置通知声音
  const handleSetNotificationSound = (sound: string) => {
    dispatch(setNotificationSound(sound));
    // 同时保存到数据库
    dispatch(updateSettings({ notificationSound: sound }));
  };
  
  // 切换通知
  const handleToggleNotifications = () => {
    dispatch(toggleNotifications());
    // 同时保存到数据库
    dispatch(updateSettings({ notificationsEnabled: !settings.notificationsEnabled }));
  };
  
  // 设置语言
  const handleSetLanguage = (lang: string) => {
    dispatch(setLanguage(lang));
    // 同时保存到数据库
    dispatch(updateSettings({ language: lang }));
  };
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: 300 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 300 }}
      className="fixed inset-y-0 right-0 w-80 bg-white dark:bg-gray-800 shadow-lg z-[80] overflow-y-auto"
    >
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">{t('settings')}</h2>
          <Button variant="text" onClick={onClose}>
            {t('close')}
          </Button>
        </div>
        
        <div className="space-y-6">
          {/* 暗色模式设置 */}
          <div className="setting-item">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                {settings.darkMode ? <FaMoon className="mr-2" /> : <FaSun className="mr-2" />}
                <span>{t('darkMode')}</span>
              </div>
              <label className="switch">
                <input 
                  type="checkbox" 
                  checked={settings.darkMode} 
                  onChange={handleToggleDarkMode}
                />
                <span className="slider round"></span>
              </label>
            </div>
          </div>
          
          {/* 语言设置 */}
          <div className="setting-item">
            <div className="flex items-center mb-2">
              <FaLanguage className="mr-2" />
              <h3 className="text-sm font-medium">{t('language')}</h3>
            </div>
            <select
              value={settings.language}
              onChange={(e) => handleSetLanguage(e.target.value)}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
            >
              <option value="en">{t('english')}</option>
              <option value="zh">{t('chinese')}</option>
              <option value="es">{t('spanish')}</option>
              <option value="fr">{t('french')}</option>
              <option value="ja">{t('japanese')}</option>
            </select>
          </div>
          
          {/* 显示毫秒设置 */}
          <div className="setting-item">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <FaClock className="mr-2" />
                <span>{t('showMilliseconds')}</span>
              </div>
              <label className="switch">
                <input 
                  type="checkbox" 
                  checked={settings.showMilliseconds} 
                  onChange={handleToggleShowMilliseconds}
                />
                <span className="slider round"></span>
              </label>
            </div>
          </div>
          
          {/* 通知设置 */}
          <div className="setting-item">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                {settings.notificationsEnabled ? <FaBell className="mr-2" /> : <FaBellSlash className="mr-2" />}
                <span>{t('enableNotifications')}</span>
              </div>
              <label className="switch">
                <input 
                  type="checkbox" 
                  checked={settings.notificationsEnabled} 
                  onChange={handleToggleNotifications}
                />
                <span className="slider round"></span>
              </label>
            </div>
          </div>
          
          {/* 通知声音设置 */}
          <div className="setting-item">
            <h3 className="text-sm font-medium mb-2">{t('notificationSound')}</h3>
            <select
              value={settings.notificationSound}
              onChange={(e) => handleSetNotificationSound(e.target.value)}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
            >
              <option value="default">{t('default')}</option>
              <option value="bell">{t('bell')}</option>
              <option value="digital">{t('digital')}</option>
              <option value="chime">{t('chime')}</option>
              <option value="none">{t('none')}</option>
            </select>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SettingsPanel;